import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import {
  Badge,
  Button,
  Input,
  NumericInput,
  Select,
  StatRow,
} from "~/components/design-system/components"
import {
  InspectorCard,
  InspectorNav,
  useInspectorExpandKey,
  useInspectorSelection,
} from "~/components/design-system/pages"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { api } from "~/utils/api"
import {
  MAX_DELIVERY_ADDRESS_LENGTH,
  MAX_SELLING_AREA,
  MAX_TRANSIT_DAYS,
  OUTLET_FORMAT,
  STORE_FIELD_LABELS,
  STORE_FORMATS,
  STORE_GRADES,
  STORE_SIZES,
  storeFormatLabel,
  type StoreField,
} from "~/utils/allocationStores"
import { pluralize } from "~/utils/formatting"
import { formatStoreValue, normalizeStoreText, type StoreValues } from "../drafts/storeDraftState"
import { PACKAGE_STATUS_META, packageWindowLabel } from "../../packageDisplay"
import { useStoreSetupDraft, type StoreSetupDraft } from "./useStoreSetupDraft"
import { StoreCoverageTimeline } from "./StoreCoverageTimeline"
import type { StoreSetupRow } from "./useStoreSetup"

const STORE_SIZE_OPTIONS = STORE_SIZES.map((size) => ({ value: size, label: size }))
const GRADE_OPTIONS = STORE_GRADES.map((grade) => ({ value: grade, label: grade }))
const FORMAT_OPTIONS = STORE_FORMATS.map(({ value, label }) => ({ value, label }))

export function StoreInspector() {
  const { selectedId } = useInspectorSelection()
  const draft = useStoreSetupDraft()
  const store = draft.stores.find((row) => row.id === selectedId)
  if (draft.isLoading) return <LoadingOverlay />
  if (!store) {
    return (
      <p className="paragraph-sm px-4 py-3 text-fg-muted">
        Choose Inspect on a store to see its weeks ahead and edit its setup.
      </p>
    )
  }
  return <StorePanel store={store} draft={draft} />
}

type PanelProps = { store: StoreSetupRow; draft: StoreSetupDraft }

function StorePanel({ store, draft }: PanelProps) {
  const staged = draft.stagedValues(store)
  const timeline = api.allocation.getStoreTimeline.useQuery({ storeId: store.id })
  const timelineKey = useInspectorExpandKey("allocation-store-timeline")
  const packagesKey = useInspectorExpandKey("allocation-store-packages")
  const infoKey = useInspectorExpandKey("allocation-store-info")
  const editedCount = Object.keys(draft.editsOf(store.id) ?? {}).length
  const packages = timeline.data?.packages ?? []

  return (
    <>
      <InspectorNav
        title={store.name}
        subtitle={[
          staged.grade && `Store grade ${staged.grade}`,
          storeFormatLabel(staged.format),
          store.country,
        ]
          .filter(Boolean)
          .join(" · ")}
        noun="stores"
      />
      <InspectorCard title="Timeline" defaultExpanded persistKey={timelineKey}>
        {timeline.data ? <StoreCoverageTimeline timeline={timeline.data} /> : <LoadingOverlay />}
      </InspectorCard>
      <InspectorCard
        title="Packages"
        badges={timeline.data ? <Badge size="xs">{packages.length}</Badge> : null}
        defaultExpanded
        persistKey={packagesKey}
      >
        {!timeline.data && <LoadingOverlay />}
        {timeline.data && packages.length === 0 && (
          <p className="paragraph-sm text-fg-muted">
            No live package lists this store, so nothing is replenished here.
          </p>
        )}
        {packages.length > 0 && (
          <ul className="flex flex-col gap-2">
            {packages.map((pkg) => (
              <li key={pkg.id} className="flex items-center gap-2">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate label-sm text-fg-base">{pkg.name}</span>
                  <span className="paragraph-xs text-fg-muted">
                    {packageWindowLabel(
                      pkg.startDate,
                      pkg.cutoffDate,
                      pkg.status === "active" ? "Always open" : "No dates",
                    )}
                  </span>
                </div>
                <Badge size="xs" variant={PACKAGE_STATUS_META[pkg.status].variant}>
                  {PACKAGE_STATUS_META[pkg.status].label}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </InspectorCard>
      <InspectorCard
        title="Store info"
        badges={
          editedCount > 0 ? (
            <Badge size="xs">{pluralize(editedCount, "unsaved change")}</Badge>
          ) : null
        }
        defaultExpanded
        persistKey={infoKey}
        contentClassName="flex flex-col gap-3"
      >
        <StoreInfoForm store={store} draft={draft} staged={staged} />
      </InspectorCard>
    </>
  )
}

function StoreInfoForm({ store, draft, staged }: PanelProps & { staged: StoreValues }) {
  const isEdited = (field: StoreField) => field in (draft.editsOf(store.id) ?? {})
  const savedNote = (field: StoreField) =>
    isEdited(field) ? `Saved: ${formatStoreValue(field, store.values[field])}` : undefined
  const navigate = useNavigate()
  const isOutlet = staged.format === OUTLET_FORMAT
  const [typedAddress, setTypedAddress] = useState<string | null>(null)

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <StatRow label="Store ID" value={store.code ?? "None"} weight="muted" />
        <StatRow label="Country" value={store.country ?? "None"} weight="muted" />
        <div className="flex items-center gap-2">
          <StatRow
            label="Replenishment rule"
            value={store.ruleId ? draft.ruleName(store.ruleId) : "No rule"}
            weight="muted"
          />
          <Button
            variant="transparent"
            size="xs"
            onClick={() =>
              void navigate({ to: "/allocation/settings/$subTab", params: { subTab: "rules" } })
            }
          >
            Change in Rules
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select
          label={STORE_FIELD_LABELS.grade}
          size="large"
          requires="administer"
          isDisabled={isOutlet}
          isClearable
          placeholder={isOutlet ? "Outlets carry no grade" : "No grade"}
          options={GRADE_OPTIONS}
          value={GRADE_OPTIONS.find((option) => option.value === staged.grade) ?? null}
          onChange={(option) => draft.stage(store, "grade", option?.value ?? null)}
          description={savedNote("grade")}
        />
        <Select
          label={STORE_FIELD_LABELS.format}
          size="large"
          requires="administer"
          options={FORMAT_OPTIONS}
          value={FORMAT_OPTIONS.find((option) => option.value === staged.format) ?? null}
          onChange={(option) => option && draft.stage(store, "format", option.value)}
          description={savedNote("format")}
        />
        <Select
          label={STORE_FIELD_LABELS.storeSize}
          size="large"
          requires="administer"
          isClearable
          placeholder="Not set"
          options={STORE_SIZE_OPTIONS}
          value={STORE_SIZE_OPTIONS.find((option) => option.value === staged.storeSize) ?? null}
          onChange={(option) => draft.stage(store, "storeSize", option?.value ?? null)}
          description={savedNote("storeSize")}
        />
        <NumericInput
          id="allocation-store-selling-area"
          label={STORE_FIELD_LABELS.sellingArea}
          requires="administer"
          variant={isEdited("sellingArea") ? "edited" : "default"}
          editedTitle={savedNote("sellingArea")}
          value={staged.sellingArea ?? ""}
          max={MAX_SELLING_AREA}
          onCommit={(value) => draft.stage(store, "sellingArea", value)}
        />
        <NumericInput
          id="allocation-store-transit-days"
          label={STORE_FIELD_LABELS.transitDays}
          requires="administer"
          variant={isEdited("transitDays") ? "edited" : "default"}
          editedTitle={savedNote("transitDays")}
          value={staged.transitDays}
          max={MAX_TRANSIT_DAYS}
          onCommit={(value) => value !== null && draft.stage(store, "transitDays", value)}
        />
      </div>
      <Input
        id="allocation-store-delivery-address"
        label={STORE_FIELD_LABELS.deliveryAddress}
        requires="administer"
        variant={isEdited("deliveryAddress") ? "edited" : "default"}
        editedTitle={savedNote("deliveryAddress")}
        value={typedAddress ?? staged.deliveryAddress ?? ""}
        placeholder="Street, postcode and city"
        maxLength={MAX_DELIVERY_ADDRESS_LENGTH}
        onChange={(event) => setTypedAddress(event.target.value)}
        onBlur={(event) => {
          setTypedAddress(null)
          draft.stage(store, "deliveryAddress", normalizeStoreText(event.target.value))
        }}
      />
    </>
  )
}
